import React from "react";
import { useCruise } from "../context/CruiseContext";
import { Button } from "./ui/button";
import { Check, Users, Wine, CalendarClock, Phone, TrendingDown } from "lucide-react";

// Estimate equal monthly payments: (price - deposit) split across the months
// remaining until the final payoff date (July 23, 2027).
function monthsUntil(dateStr) {
  const now = new Date();
  const target = new Date(dateStr + "T00:00:00");
  let months =
    (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
  if (target.getDate() > now.getDate()) months += 1; // partial month counts
  return Math.max(months, 1);
}

function estimateMonthly(priceStr, payment) {
  const price = parseFloat(priceStr.replace(/[^0-9.]/g, ""));
  const balance = Math.max(price - payment.depositPerPerson, 0);
  const months = monthsUntil(payment.payoffDate);
  const monthly = balance / months;
  return monthly.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

const Pricing = () => {
  const { CABINS, BOOKING_BASE, EVENT, PAYMENT } = useCruise();
  const months = monthsUntil(PAYMENT.payoffDate);
  const payoffPassed = new Date(`${PAYMENT.payoffDate}T23:59:59`) < new Date();
  const chips = payoffPassed
    ? [{ icon: Phone, t: `Full payment required \u2013 call ${EVENT.phone}` }]
    : [
        { icon: CalendarClock, t: `Deposit ${EVENT.depositFrom}/person` },
        { icon: TrendingDown, t: `~${months} equal monthly payments` },
        { icon: CalendarClock, t: `Final payoff ${EVENT.finalPayoff}` },
      ];
  return (
    <section id="pricing" className="relative bg-[#0A0A0A] py-24">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center mb-6">
          <p className="font-cond uppercase tracking-[0.4em] text-[#E50914] text-sm mb-3">Choose Your Cabin</p>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl">
            Cabins & <span className="text-[#E50914]">Pricing</span>
          </h2>
          <div className="flex justify-center mt-4">
            <span className="section-line" />
          </div>
          <p className="mt-5 text-white/60 max-w-2xl mx-auto">
            Prices are per person based on double occupancy.{" "}
            {payoffPassed
              ? `The EZ Monthly Payment Plan has closed — full payment is now required to secure your cabin. Call ${EVENT.phone} for current pricing and availability.`
              : `Reserve with a ${EVENT.depositFrom} per-person deposit and split the balance with the EZ Monthly Payment Plan.`}
          </p>
        </div>

        {/* highlight bar */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {chips.map((b) => (
            <div key={b.t} data-testid="pricing-chip" className="flex items-center gap-2 bg-[#1A1A1A] border border-white/10 rounded-full px-4 py-2">
              <b.icon size={16} className="text-[var(--hh-gold)]" />
              <span className="font-cond uppercase text-white/80 text-xs tracking-widest">{b.t}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CABINS.map((cabin, i) => (
            <div
              key={`${cabin.name}-${i}`}
              className={`relative flex flex-col bg-[#111111] border rounded-2xl transition-all duration-300 hover:-translate-y-1.5 ${
                cabin.featured ? "border-[#E50914]" : "border-white/10 hover:border-white/25"
              }`}
            >
              {cabin.drinks && (
                <span className="absolute top-0 right-0 bg-[#1B8A5A] text-white font-cond uppercase text-[10px] tracking-widest px-3 py-1 rounded-tr-2xl rounded-bl-xl flex items-center gap-1">
                  <Wine size={12} /> Drinks Included
                </span>
              )}
              {cabin.featured && !cabin.drinks && (
                <span className="absolute top-0 right-0 bg-[#E50914] text-white font-cond uppercase text-[10px] tracking-widest px-3 py-1 rounded-tr-2xl rounded-bl-xl">
                  Popular
                </span>
              )}

              <div className="p-6 flex flex-col h-full">
                <h3 className="font-cond font-bold uppercase text-white text-xl leading-tight tracking-wide pr-6">
                  {cabin.name}
                </h3>
                <div className="flex items-center gap-2 mt-1 text-white/40">
                  <Users size={13} />
                  <span className="text-xs font-cond uppercase tracking-widest">{cabin.guests}</span>
                  <span className="text-white/20">|</span>
                  <span className="text-xs">{cabin.size}</span>
                </div>

                <div className="mt-5 mb-1">
                  <span className="font-display text-white text-4xl">{cabin.price}</span>
                </div>
                <p className="text-[var(--hh-gold)] font-cond uppercase text-[11px] tracking-widest">per person / dbl occ.</p>

                {!payoffPassed && (
                <div data-testid="pricing-monthly-estimate" className="mt-3 flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2">
                  <TrendingDown size={15} className="text-[#1B8A5A] shrink-0" />
                  <span className="text-white/85 text-sm font-cond">
                    <span className="text-white font-bold">{estimateMonthly(cabin.price, PAYMENT)}</span>
                    <span className="text-white/50">/mo est. after {EVENT.depositFrom} deposit</span>
                  </span>
                </div>
                )}

                <p className="text-white/55 text-sm mt-4">{cabin.desc}</p>

                <ul className="mt-4 space-y-2 flex-1">
                  {cabin.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-white/75 text-sm">
                      <Check size={15} className="text-[#1B8A5A] mt-0.5 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={`${BOOKING_BASE}${cabin.token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6"
                >
                  <Button
                    className={`w-full rounded-full font-cond uppercase tracking-widest ${
                      cabin.featured
                        ? "bg-[#E50914] hover:bg-[#c20710] text-white"
                        : "bg-white/10 hover:bg-[#E50914] text-white"
                    }`}
                  >
                    Book This Cabin
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-white/50 text-sm max-w-3xl mx-auto">
            Prices, availability and cabin categories are confirmed at time of booking.{" "}
            {payoffPassed
              ? "Full payment is required at booking (excludes processing fees & gratuities)."
              : `Monthly amounts are estimates only — balance after the ${EVENT.depositFrom} deposit split across the months to the ${EVENT.finalPayoff} payoff (excludes processing fees & gratuities).`}{" "}
            Deposits are non-refundable.
            Questions? Call{" "}
            <a href={EVENT.phoneHref} className="text-[#E50914] font-semibold inline-flex items-center gap-1">
              <Phone size={13} /> {EVENT.phone}
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
